/**
 * NakshatramshaInfoTable Component
 * Displays D27 Nakshatramsha (Bhamsha) division information
 * 27 divisions per rashi based on nakshatra system
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  NAKSHATRAMSHA_ATTRIBUTES,
  calculateNakshatramshaData,
  PlanetD27Data
} from '../data/NakshatramshaTableData';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import {
  TABLE_WRAPPER_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface NakshatramshaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group planets by amsha number (1-27)
 */
function groupPlanetsByAmsha(planets: PlanetD27Data[]): Record<number, PlanetD27Data[]> {
  const planetsByAmsha: Record<number, PlanetD27Data[]> = {};

  // Initialize all amshas with empty arrays
  for (let i = 1; i <= 27; i++) {
    planetsByAmsha[i] = [];
  }

  // Group planets by their amsha
  planets.forEach(planet => {
    planetsByAmsha[planet.amshaNumber].push(planet);
  });

  return planetsByAmsha;
}

export const NakshatramshaInfoTable: React.FC<NakshatramshaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  const planetsData = calculateNakshatramshaData(apiResponse);
  const planetsByAmsha = groupPlanetsByAmsha(planetsData);

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  return (
    <Box {...TABLE_WRAPPER_STYLES}>
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.small}>Planets</Th>
              <Th {...COLUMN_STYLES.small} textAlign="center">Degrees</Th>
              <Th {...COLUMN_STYLES.small} textAlign="center">Amsha</Th>
              <Th {...COLUMN_STYLES.small}>Nakshatra</Th>
              <Th {...COLUMN_STYLES.small}>Deity</Th>
              <Th {...COLUMN_STYLES.small} textAlign="center">Bhamsha</Th>
              <Th {...COLUMN_STYLES.small} textAlign="center">Ruler</Th>
            </Tr>
          </Thead>
          <Tbody>
            {NAKSHATRAMSHA_ATTRIBUTES.map((amshaData) => {
              const planetsInAmsha = planetsByAmsha[amshaData.amsha] || [];

              return (
                <Tr key={amshaData.amsha} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="30px">
                    {planetsInAmsha.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {planetsInAmsha.map((planet, index) => {
                          // Determine planet type for color configuration (priority order)
                          let planetType: 'regular' | 'lagna' | 'hora' | 'chandra' | 'retrograde' = 'regular';

                          if (planet.isLagna) {
                            planetType = 'lagna';
                          } else if (planet.isHora) {
                            planetType = 'hora';
                          } else if (planet.isChandra) {
                            planetType = 'chandra';
                          } else if (planet.isRetrograde) {
                            planetType = 'retrograde';
                          }

                          const colors = getAshtamshaPlanetColor(planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          // Display text with retrograde indication
                          const displayText = planet.isRetrograde && !planet.isLagna
                            ? `(${planet.shortName})`
                            : planet.shortName;

                          return (
                            <Text
                              key={`${planet.shortName}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Degrees column */}
                  <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal" fontSize="10px" textAlign="center">
                    {amshaData.degreeStart.padStart(9, '\u00A0')} - {amshaData.degreeEnd.padStart(10, '\u00A0')}
                  </Td>

                  {/* Amsha number column */}
                  <Td {...TABLE_TD_BASE_STYLES} textAlign="center" fontWeight="normal">
                    {amshaData.amsha}
                  </Td>

                  {/* Nakshatra column */}
                  <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">
                    {amshaData.nakshatra}
                  </Td>

                  {/* Deity column */}
                  <Td {...TABLE_TD_BASE_STYLES} fontWeight="normal">
                    {amshaData.deity}
                  </Td>

                  {/* Bhamsha house number column */}
                  <Td {...TABLE_TD_BASE_STYLES} textAlign="center" fontWeight="normal" fontSize="xs">
                    {planetsInAmsha.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap" justifyContent="center">
                        {planetsInAmsha.map((planet, index) => (
                          <Text key={`bhamsha-${planet.shortName}-${index}`} fontSize="xs">
                            {planet.bhamshaRashi}
                          </Text>
                        ))}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Ruler column (ruler of bhamsha rashi) */}
                  <Td {...TABLE_TD_BASE_STYLES} textAlign="center" fontWeight="normal" fontSize="xs">
                    {planetsInAmsha.length > 0 ? (
                      <HStack spacing={0.5} flexWrap="wrap" justifyContent="center">
                        {planetsInAmsha.map((planet, index) => {
                          const rulerShortName = planet.bhamshaRashiRuler;

                          // Find corresponding planet in planetsData to get isHora flag
                          const rulerPlanetData = planetsData.find(p => p.shortName === rulerShortName);

                          // Determine planet type for ruler
                          let rulerType: 'regular' | 'lagna' | 'hora' | 'chandra' | 'retrograde' = 'regular';

                          if (rulerPlanetData) {
                            if (rulerPlanetData.isLagna) {
                              rulerType = 'lagna';
                            } else if (rulerPlanetData.isHora) {
                              rulerType = 'hora';
                            } else if (rulerPlanetData.isChandra) {
                              rulerType = 'chandra';
                            }
                          } else {
                            // Fallback for rulers that are not in the chart (shouldn't happen)
                            if (rulerShortName === 'Lg') {
                              rulerType = 'lagna';
                            } else if (rulerShortName === 'Ch') {
                              rulerType = 'chandra';
                            }
                          }

                          const colors = getAshtamshaPlanetColor(rulerType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`ruler-${planet.shortName}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {rulerShortName}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
